import { isDefinedArray, isNullOrUndefined } from "et3-type-guards";
import { memoize } from "lodash";
import { observable, runInAction } from "mobx";
import { apiCallUpdateFeature } from "../../../api/developer-api/api-call-update-feature";
import { apiCallGetKhattabatFeaturesData } from "../../../api/khattbat-api/api-call-get-khattabat-features-data";
import { apiCallGetKhattabatAvailability } from "../../../api/khattbat-api/api-call-get-khattabats-availability";
import { apiCallUpdateKhattabatAvailability } from "../../../api/khattbat-api/api-call-update-khattabat-availability";
import { apiCallUpdateRCCapacity } from "../../../api/khattbat-api/api-call-update-khattabat-capacity";
import { apiCallUpdateROFeatures } from "../../../api/khattbat-api/api-call-update-khattabat-capacity copy";
import { apiCallGetFunnelStatsData } from "../../../api/supervisor-api/api-call-get-khattabat-funnel-stats-data";
import { getSpecificDateWithDays } from "../../../core/et3-date";
import { isDateRangeValid } from "../../../helpers/et3-date-helper";
import {
  TDbRolloutPercentage,
  TFeature,
  TGender,
  TKhattabatFunnelStatsData
} from "../../../Types";
import {
  TEt3PickerSingleValue,
  TKhattabatsAvailability
} from "../../../TypesNEW";
import getCorePopUpStore from "../../core/core-popup-store";
import getCoreUIStore from "../../core/core-ui-store";

class SupervisorKhattabatStore {
  khattabatFeatures = observable.box<TFeature[]>([]);
  khattabatAvailability = observable.box<TKhattabatsAvailability>([]);
  funnelStatsData = observable.box<TKhattabatFunnelStatsData[]>([]);
  funnelStatsFromDate = observable.box<Date>(
    getSpecificDateWithDays({
      daysSubtracted: 1,
      daysAdded: 0
    })
  );
  funnelStatsToDate = observable.box<Date>(
    getSpecificDateWithDays({ daysSubtracted: 0, daysAdded: 1 })
  );
  khattabatFeaturesRollOutPickersData = observable.map<
    number,
    TEt3PickerSingleValue
  >({});

  setKhattabatAvailability = (availibility: TKhattabatsAvailability) => {
    const formatedData = availibility.map((details) => {
      return {
        ...details,
        updatedCapacityCount: details.capacity
      };
    });
    runInAction(() => {
      this.khattabatAvailability.set(formatedData);
    });
  };

  setFeatures = (features: TFeature[]) => {
    runInAction(() => {
      this.khattabatFeatures.set(features);
    });
  };

  setFunnelStatsData = (data: TKhattabatFunnelStatsData[]) => {
    runInAction(() => {
      this.funnelStatsData.set(data);
    });
  };

  clearKhattabatFeaturesRollOutPickersData = () => {
    runInAction(() => {
      this.khattabatFeaturesRollOutPickersData.clear();
    });
  };

  setKhattabatFeatureRollOutPickersData = ({
    featureId,
    value
  }: {
    featureId: number;
    value: TEt3PickerSingleValue;
  }) => {
    runInAction(() => {
      this.khattabatFeaturesRollOutPickersData.set(featureId, value);
    });
  };

  initializeKhattabatFeaturesRollOutPickersData = (features: TFeature[]) => {
    if (!isDefinedArray(features)) {
      return;
    }

    features.forEach((feature) => {
      runInAction(() => {
        this.khattabatFeaturesRollOutPickersData.set(feature.featureId, {
          label: `${feature.feature_rollout_percentage}`,
          value: `${feature.feature_rollout_percentage}`
        });
      });
    });
  };

  isKhattabatFeatureRollOutPickersDataUpdateExists = (featureId: number) => {
    const clientPickerValue = <TDbRolloutPercentage>(
      this.khattabatFeaturesRollOutPickersData.get(featureId).value
    );
    const dbValue = this.khattabatFeatures.get().filter((feature) => {
      return feature.featureId === featureId;
    })[0].feature_rollout_percentage;

    return Number(clientPickerValue) != Number(dbValue);
  };

  getKhattabatFeatures = async () => {
    getCoreUIStore().setIsLoading("SUPERVISOR_TURN_ON_OFF_KHATTABAT", true);
    const { status, data, code } = await apiCallGetKhattabatFeaturesData();
    getCoreUIStore().setIsLoading("SUPERVISOR_TURN_ON_OFF_KHATTABAT", false);
    if (status === "OK") {
      const editedData = data.map((item) => ({
        ...item,
        feature_action: {
          id: item.featureId,
          is_active: item.is_active,
          gender: item.gender
        }
      }));

      this.setFeatures(editedData);
      this.initializeKhattabatFeaturesRollOutPickersData(editedData);
    } else {
      getCorePopUpStore().showAlertMessage(code);
    }
  };

  getKhattabatAvailabilityFeature = async () => {
    getCoreUIStore().setIsLoading("SUPERVISOR_TURN_ON_OFF_KHATTABAT", true);
    const { status, data, code } = await apiCallGetKhattabatAvailability();
    getCoreUIStore().setIsLoading("SUPERVISOR_TURN_ON_OFF_KHATTABAT", false);
    if (status === "OK") {
      this.setKhattabatAvailability(data);
    } else {
      getCorePopUpStore().showAlertMessage(code);
    }
  };

  khattabatAvailabilityUpdate = async (
    adminID: number,
    isAvailable: number
  ) => {
    const newStatus = isAvailable === 1 ? 0 : 1;
    getCoreUIStore().setIsLoading("SUPERVISOR_TURN_ON_OFF_KHATTABAT", true);
    const { status, code } = await apiCallUpdateKhattabatAvailability(
      adminID,
      newStatus
    );
    getCoreUIStore().setIsLoading("SUPERVISOR_TURN_ON_OFF_KHATTABAT", false);
    if (status === "OK") {
      const updatedKhattabatAvailability = this.khattabatAvailability
        .get()
        .map((khattabat) => {
          if (khattabat.adminId === adminID) {
            return {
              ...khattabat,
              isAvailable: newStatus
            };
          } else {
            return khattabat;
          }
        });
      this.setKhattabatAvailability(updatedKhattabatAvailability);
    } else {
      getCorePopUpStore().showAlertMessage(code);
    }
  };

  updateKhattabatFeature = async (
    id: number,
    featureName: string,
    isActive: number,
    gender: string
  ) => {
    getCoreUIStore().setIsLoading("SUPERVISOR_TURN_ON_OFF_KHATTABAT", true);
    const newStatus = isActive === 1 ? 0 : 1;
    const typedGender: TGender = gender === "male" ? "m" : "f";
    const { status, code } = await apiCallUpdateFeature(
      id,
      featureName,
      newStatus,
      typedGender
    );
    if (status === "OK") {
      const updatedFeatures = this.khattabatFeatures.get().map((feature) => {
        if (feature.featureId === id && feature.gender === gender) {
          return {
            ...feature,
            is_active: newStatus,
            feature_action: { id, is_active: newStatus, gender }
          };
        } else {
          return feature;
        }
      });
      this.setFeatures(updatedFeatures);
      getCoreUIStore().setIsLoading("SUPERVISOR_TURN_ON_OFF_KHATTABAT", false);
    } else {
      getCoreUIStore().setIsLoading("SUPERVISOR_TURN_ON_OFF_KHATTABAT", false);
      getCorePopUpStore().showAlertMessage(code);
      if (code === "KHATTABA_V3_IS_ALREADY_TURNED_OFF") {
        await this.getKhattabatFeatures();
      }
    }
  };

  getAllRequiredData = async () => {
    await this.getKhattabatFeatures();
    await this.getKhattabatAvailabilityFeature();
  };

  updateRCCapacityCountClient({
    adminId,
    updatedCapacityValue
  }: {
    adminId: number;
    updatedCapacityValue: number;
  }): void {
    const item = this.khattabatAvailability
      .get()
      .find((entry) => entry.adminId === adminId);
    if (!isNullOrUndefined(item)) {
      item.updatedCapacityCount = updatedCapacityValue;
    }
  }

  async updateKhattabaCapacityOnDatabase({
    adminID,
    updatedCapacityCount
  }: {
    adminID: number;
    updatedCapacityCount: number;
  }) {
    getCoreUIStore().setIsLoading("SUPERVISOR_TURN_ON_OFF_KHATTABAT", true);
    const { status, code } = await apiCallUpdateRCCapacity({
      adminID,
      updatedCapacityCount
    });
    getCoreUIStore().setIsLoading("SUPERVISOR_TURN_ON_OFF_KHATTABAT", false);
    if (status === "OK") {
      await this.getKhattabatAvailabilityFeature();
    } else {
      getCorePopUpStore().showAlertMessage(code);
    }
  }

  getFunnelStatsData = async () => {
    this.setFunnelStatsData([]);

    if (
      !isDateRangeValid({
        fromDate: this.funnelStatsFromDate.get(),
        toDate: this.funnelStatsToDate.get(),
        isLimitedToOneMonth: false
      })
    ) {
      return;
    }
    getCoreUIStore().setIsLoading("SUPERVISOR_KHATTABAT_FUNNEL_STATS", true);
    const accountsDeletionData = await apiCallGetFunnelStatsData({
      startDate: this.funnelStatsFromDate.get(),
      endDate: this.funnelStatsToDate.get()
    });

    getCoreUIStore().setIsLoading("SUPERVISOR_KHATTABAT_FUNNEL_STATS", false);
    const { data, status, code } = accountsDeletionData;
    if (status === "ERROR") {
      return getCorePopUpStore().showAlertMessage(code);
    }

    this.setFunnelStatsData([data]);
  };

  async updateFeatureRolloutPercentageOnDatabase({
    featureId,
    newRO
  }: {
    featureId: number;
    newRO: string;
  }) {
    getCoreUIStore().setIsLoading("SUPERVISOR_TURN_ON_OFF_KHATTABAT", true);
    const { status, code } = await apiCallUpdateROFeatures({
      featureId,
      newRO
    });
    getCoreUIStore().setIsLoading("SUPERVISOR_TURN_ON_OFF_KHATTABAT", false);
    if (status === "OK") {
      runInAction(() => {
        this.khattabatFeatures.get().forEach((feature, index) => {
          if (feature.featureId === featureId) {
            this.khattabatFeatures.get()[index].feature_rollout_percentage = <
              TDbRolloutPercentage
            >`${newRO}`;
          }
        });
      });
    } else {
      getCorePopUpStore().showAlertMessage(code);
    }
  }
}

export const getSupervisorKhattabatStore = memoize(
  () => {
    return new SupervisorKhattabatStore();
  },
  () => 1
);

export default getSupervisorKhattabatStore;
